import PrayerTimingList from "@/components/panels/PrayerTimingList";
import { cardClassName } from "@/lib/constants";
import { formatClock, formatCountdown } from "@/lib/formatting";
import { useNow } from "@/lib/hooks/use-now";
import { getPrayerMethodLabel } from "@/lib/prayer-methods";
import type { PrayerLocation, PrayerMethodConfig, PrayerSnapshot } from "@/types/prayer";

type PrayerSummaryCardProps = {
  isFocused: boolean;
  location: PrayerLocation | null;
  methodConfig: PrayerMethodConfig;
  onResetFocus: () => void;
  snapshot: PrayerSnapshot | null;
};

const PrayerSummaryCard = ({ isFocused, location, methodConfig, onResetFocus, snapshot }: PrayerSummaryCardProps) => {
  const now = useNow();
  const activeTiming = snapshot?.activeEvent
    ? snapshot.timings.find((timing) => timing.event === snapshot.activeEvent)
    : null;
  const nextTiming = snapshot?.nextEvent
    ? snapshot.timings.find((timing) => timing.event === snapshot.nextEvent)
    : null;
  const countdown =
    snapshot?.nextEventTime && snapshot.nextEventTime.getTime() > now.getTime()
      ? formatCountdown(snapshot.nextEventTime.getTime() - now.getTime())
      : null;

  if (!location || !snapshot) {
    return (
      <div className={`${cardClassName} px-5 py-4`}>
        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Prayer Summary</p>
        <p className="mt-3 text-sm text-zinc-400">Select a city to inspect the live prayer state.</p>
      </div>
    );
  }

  return (
    <div className={`${cardClassName} max-h-[min(42rem,calc(100vh-5rem))] overflow-hidden`}>
      <div className="flex max-h-full flex-col px-5 py-4">
        <div className="shrink-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Current Prayer</p>

          <div className="mt-3">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-emerald-400">{activeTiming?.label ?? "Pending"}</span>
              {countdown && <span className="font-mono text-xs text-emerald-500/70">{countdown}</span>}
            </div>
            <span className="text-[10px] text-zinc-500">now in {location.city}</span>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-full border border-zinc-800 bg-zinc-900/50 px-2 py-0.5 text-[10px] font-medium text-zinc-400">
              {snapshot.timezoneLabel}
            </span>
            <span className="rounded-full border border-emerald-500/20 bg-emerald-500/5 px-2 py-0.5 text-[10px] font-medium text-emerald-400">
              {formatClock(now, location.timeZone)}
            </span>
            <span className="rounded-full border border-sky-500/20 bg-sky-500/5 px-2 py-0.5 text-[10px] font-medium text-sky-300">
              {getPrayerMethodLabel(methodConfig.method)}
            </span>
          </div>
        </div>

        <div className="mt-3 shrink-0 border-t border-zinc-800/50 pt-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[10px] text-zinc-500">Next prayer</span>
            <div className="text-right">
              <div className="font-semibold text-sky-400 text-sm">{nextTiming?.label ?? "Recomputing"}</div>
              <div className="text-[10px] text-zinc-600">{nextTiming?.time ?? "Pending"}</div>
            </div>
          </div>
        </div>

        {isFocused && (
          <div className="mt-3 min-h-0 flex-1 border-t border-zinc-800/50 pt-3">
            <div className="flex h-full min-h-0 flex-col">
              <div className="shrink-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">Focused Day</p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-wide text-zinc-400">
                  <span className="rounded-full bg-white/10 px-3 py-1 font-semibold text-zinc-100">
                    {snapshot.dateLabel}
                  </span>
                  <span className="rounded-full bg-white/5 px-3 py-1 font-medium text-zinc-300">{location.city}</span>
                </div>
              </div>

              <div className="mt-3 min-h-0 overflow-y-auto pr-1">
                <PrayerTimingList activeEvent={snapshot.activeEvent} timings={snapshot.timings} variant="compact" />
              </div>
            </div>
          </div>
        )}

        <div className="mt-3 shrink-0 border-t border-zinc-800/50 pt-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[10px] text-zinc-500">Coordinates</p>
              <p className="font-mono text-[11px] text-zinc-300">
                {location.lat.toFixed(4)}°, {location.lon.toFixed(4)}°
              </p>
            </div>
            {isFocused && (
              <button
                className="cursor-pointer rounded-full border border-zinc-700 bg-zinc-900/80 px-3 py-1 text-xs text-zinc-300 transition-colors hover:border-zinc-600 hover:bg-zinc-800"
                onClick={onResetFocus}
                type="button"
              >
                Reset Focus
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerSummaryCard;
